import type { ItemResponse, OrganisationResponse } from "@finn-no/mobility-dealer-types";
import type { EidResponse, MobilityAd } from "@mobility-item-page/types";
import { createContext, type FC, type ReactNode, useContext } from "react";
import type { ContactFormVariant } from "../../types/contactFormVariants.ts";
import type { CommonDealerProps } from "../../types.ts";
import { type DealerItemResult, DealerItemStatus, type ErrorStatus } from "../../types.ts";
import { OrganisationContext } from "./OrganisationContext.tsx";

export type ExternalProps = CommonDealerProps & {
  adViewType?: MobilityAd["adViewType"];
  isLoggedIn: boolean;
  isPreview: boolean;
  eidAuth: EidResponse;
  openContactForm: boolean;

  // Features
  homepageSurvey: boolean;
  reservationEnabled: boolean;
  isItemReserved: boolean;
  newContactForm: boolean;
  newContactFormV2: boolean;
  newContactFormV3: ContactFormVariant | undefined;
  forcePhoneExperiment: boolean;
};
type ResolvedProps = {
  phoneNumberCallbackUrl?: string;
};
/**
 * This React Context provides the data fetched from the "mobility company profile" api
 */
export type Context = {
  dealerItemResult: DealerItemResult | null;
  external: ExternalProps;
};

const DealerItemContext = createContext<Context>({
  dealerItemResult: null,
  external: {
    isMobile: false,
    brand: "FINN",
    locale: "no",
    isLoggedIn: false,
    isPreview: false,
    adViewType: undefined,
    homepageSurvey: false,
    reservationEnabled: false,
    isItemReserved: false,
    eidAuth: { status: "NOT_APPLICABLE", userMustVerify: false },
    newContactForm: false,
    newContactFormV2: false,
    newContactFormV3: undefined,
    forcePhoneExperiment: false,
    openContactForm: false,
  },
});

type Props = {
  dealerItemResult: DealerItemResult | null;
  external: ExternalProps;
  children: ReactNode;
};
export function DealerItemDataProvider(props: Props) {
  const { children, dealerItemResult, external } = props;
  const organisationData =
    dealerItemResult?.status === DealerItemStatus.Ok ? (dealerItemResult.data?.organisationData ?? null) : null;
  const organisationContextValue = organisationData
    ? {
        organisation: organisationData,
        common: { locale: external.locale, brand: external.brand, isMobile: external.isMobile },
      }
    : null;
  return (
    <DealerItemContext value={{ dealerItemResult, external }}>
      <OrganisationContext value={organisationContextValue}>{children}</OrganisationContext>
    </DealerItemContext>
  );
}

type ValidDealerItemContext = {
  status: DealerItemStatus.Ok;
  itemData: Omit<ItemResponse, "organisationData">;
  organisationData: OrganisationResponse;
  external: ExternalProps;
  resolved: ResolvedProps;
};

type InValidDealerItemContext = {
  status: ErrorStatus;
  external: ExternalProps;
};

const isWrappedInDealerItemProviderCheck = (dealerItemResult: DealerItemResult | null) => {
  return dealerItemResult !== null;
};

export function useDealerItemContext(): ValidDealerItemContext | InValidDealerItemContext {
  const { dealerItemResult, external } = useContext(DealerItemContext);
  if (
    !isWrappedInDealerItemProviderCheck ||
    !dealerItemResult?.status ||
    dealerItemResult.status === DealerItemStatus.NotDealerAd
  ) {
    throw new Error(
      `dealerData (${dealerItemResult}) is not defined. Ensure you are within a DealerItemDataProvider and that your component is wrapped in "withDataGuard(...Component)"`,
    );
  }

  const { status } = dealerItemResult;
  if (status !== DealerItemStatus.Ok) {
    return { status, external };
  }

  const { data } = dealerItemResult;
  const { organisationData, ...itemData } = data ?? {};

  const resolved: ResolvedProps = {
    phoneNumberCallbackUrl: itemData.integrityToken
      ? `/mobility/api/phone/${itemData.adId}/${itemData.integrityToken}`
      : undefined,
  };

  return { status, organisationData, itemData, external, resolved };
}

// Expecting that dealerItemResult status is "ok"
export function useValidDealerItemContext(): ValidDealerItemContext {
  const { dealerItemResult, external } = useContext(DealerItemContext);
  if (!isWrappedInDealerItemProviderCheck) {
    throw new Error(
      `dealerData (${dealerItemResult}) is not defined. Ensure you are within a DealerItemDataProvider and that your component is wrapped in "withDataGuard(...Component)"`,
    );
  }
  if (!dealerItemResult?.status || dealerItemResult.status !== DealerItemStatus.Ok) {
    throw new Error(
      `dealerData (${dealerItemResult}) Status is not "ok". Received: ${dealerItemResult?.status}. Use useDealerItemContext when you want to handle these cases.`,
    );
  }
  const { status, data } = dealerItemResult;
  const { organisationData, ...itemData } = data ?? {};

  const resolved: ResolvedProps = {
    phoneNumberCallbackUrl: itemData.integrityToken
      ? `/mobility/api/phone/${itemData.adId}/${itemData.integrityToken}`
      : undefined,
  };

  return { status, organisationData, itemData, external, resolved };
}

// Returns item data without organisationData or null if not available. This is safe to use outside of data guards.
export function useSafeDealerItemContext(): Omit<ItemResponse, "organisationData"> | null {
  const { dealerItemResult } = useContext(DealerItemContext);

  if (!isWrappedInDealerItemProviderCheck) {
    return null;
  }

  if (dealerItemResult?.status === "ok") {
    const { organisationData, ...itemData } = dealerItemResult.data ?? {};
    return itemData;
  }

  return null;
}

export const withDataGuard = <Props,>(Component: FC<Props>): FC<Props> => {
  return (props) => {
    const { dealerItemResult } = useContext(DealerItemContext);

    if (dealerItemResult === null || dealerItemResult?.status !== DealerItemStatus.Ok) {
      return null;
    }

    return Component(props);
  };
};

export const withDataGuardFallback = <Props,>(Component: FC<Props>, fallback?: () => ReactNode): FC<Props> => {
  return (props) => {
    const { status } = useDealerItemContext();

    if (status === DealerItemStatus.NotDealerAd) {
      return null;
    }
    if (status !== DealerItemStatus.Ok) {
      if (fallback) {
        return fallback();
      }
      return null;
    }

    return Component(props);
  };
};
